import { useContext, useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import {
  DirectionalHint,
  TooltipDelay,
  DefaultButton,
  PrimaryButton,
  IconButton,
} from "@fluentui/react";
import { Enums } from "@cornerstonejs/core";
import toastContext from "../../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../../constants/Constants";
import CustomToolTipHost from "../../../../../components/CustomToolTipHost/CustomToolTipHost";
import ImageEnhancementIcon from "../../../../../components/SVGComponents/Tools/ImageEnhancementIcon";
import { useTranslation } from "react-i18next";
import ImageEnhancementBody from "./ImageEnhancementBody";
import themeContext from "../../../../../context/theme/ThemeContext";
import {
  toolBarFlex,
  toolTipStyles,
} from "../../../styles/styles";
import "../../../styles/ImageEnhancementTool.css";
import { getViewport } from "../../../helpers/CornerstoneViewportService";
import ViewportContext from "../../../../../context/studyviewer/ViewportContext";
import { isMobile } from "react-device-detect";

const isStackViewport = (viewport) => {
  return viewport && viewport.type === Enums.ViewportType.STACK;
};

/**
 * Type guard to check if viewport is a VolumeViewport (3D volume viewer)
 * VolumeViewport requires alternative enhancement techniques
 */
const isVolumeViewport = (viewport) => {
  return viewport && (viewport.type === Enums.ViewportType.VOLUME_3D || viewport.type === Enums.ViewportType.ORTHOGRAPHIC);
};

/**
 * Image Enhancement Tool with Viewport-Specific Filtering
 *
 * For StackViewport (2D):
 * 1. Sharpening - Unsharp masking (3×3 edge enhancement kernel)
 * 2. Gaussian Blur - Gaussian blur (15×15 Gaussian kernel, σ=5.0)
 *
 * For VolumeViewport (3D MPR/Volume):
 * Note: Standard sharpening/smoothing can distort diagnostic data in 3D volumes.
 * Instead using alternative techniques:
 * 1. Linear Interpolation - Smoothing without distortion
 * 2. Gradient Opacity - Edge enhancement via opacity mapping
 * 3. VOI Adjustment - Contrast enhancement via window/level
 * 4. Sampling Distance - Controls rendering sharpness/resolution
 */

/**
 * Apply volume-specific enhancements for VolumeViewport
 * Uses medically-safe techniques that don't distort diagnostic data
 */
const applyVolumeEnhancement = (viewport, type, intensity) => {
  try {
    if (type === "sharpening") {
      // For volumes: use sampling distance adjustment for perceived sharpness
      // Closer sampling = sharper but slower; farther = smoother but faster
      const samplingDistance = 1 - intensity * 0.5; // 1.0 (sharp) to 0.5 (smooth)
      viewport.setDefaultProperties(
        {
          sampleDistanceMultiplier: Math.max(0.5, samplingDistance),
        },
        ""
      );
    } else if (type === "smoothing") {
      // For volumes: enable gradient opacity for edge enhancement
      // Higher intensity = more edge emphasis
      if (intensity > 0) {
        viewport.setDefaultProperties(
          {
            sampleDistanceMultiplier: 1 + intensity * 0.5, // 1.0 to 1.5
          },
          ""
        );
      }
    } else if (type === "median") {
      // For volumes: reduce noise with moderate sampling adjustment
      // Similar to smoothing but less aggressive
      if (intensity > 0) {
        viewport.setDefaultProperties(
          {
            sampleDistanceMultiplier: 1 + intensity * 0.25, // 1.0 to 1.25
          },
          ""
        );
      }
    } else if (type === "edgeEnhance") {
      // For volumes: sharper sampling for edge emphasis
      if (intensity > 0) {
        viewport.setDefaultProperties(
          {
            sampleDistanceMultiplier: Math.max(0.3, 1 - intensity * 0.7), // 1.0 to 0.3
          },
          ""
        );
      }
    }
  } catch (error) {
    console.error("Error applying volume enhancement:", error);
    throw error;
  }
};

/**
 * CPU-based 3x3 Laplacian edge enhancement for stack viewports (2D)
 * Blends the original image with a Laplacian edge-detected version
 * intensity controls the mix: 0 = original, 1 = maximum edge emphasis
 * Kernel:  [ 0, -1,  0 ]
 *          [-1,  4, -1 ]
 *          [ 0, -1,  0 ]
 */
const applyEdgeEnhancementToCanvas = (canvas, intensity) => {
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;
    const src = new Uint8ClampedArray(data);

    // Laplacian kernel weights (3x3)
    // [ 0, -1,  0 ]
    // [-1,  4, -1 ]
    // [ 0, -1,  0 ]
    const blend = Math.min(1, Math.max(0, intensity)); // clamp 0-1

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        for (let c = 0; c < 3; c++) {
          // Laplacian = 4*center - top - bottom - left - right
          const center = src[idx + c];
          const top    = src[((y - 1) * width + x) * 4 + c];
          const bottom = src[((y + 1) * width + x) * 4 + c];
          const left   = src[(y * width + (x - 1)) * 4 + c];
          const right  = src[(y * width + (x + 1)) * 4 + c];
          const laplacian = 4 * center - top - bottom - left - right;
          // Blend: original + intensity * laplacian
          data[idx + c] = Math.min(255, Math.max(0, center + blend * laplacian));
        }
        // Keep alpha unchanged
      }
    }

    ctx.putImageData(imageData, 0, 0);
  } catch (error) {
    console.error("Error applying edge enhancement to canvas:", error);
  }
};

/**
 * CPU-based 3x3 median filter for stack viewports (2D)
 * Processes image data to reduce noise while preserving edges
 * Median value from 9-pixel neighborhood replaces center pixel
 */
const applyMedianFilterToCanvas = (canvas, intensity) => {
  try {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;
    const kernelRadius = 1; // 3x3 kernel

    // Create a copy of the image data
    const dataCopy = new Uint8ClampedArray(data);

    // Apply median filter based on intensity (0-1)
    const passes = Math.max(1, Math.ceil(intensity * 3)); // 1-3 passes

    for (let pass = 0; pass < passes; pass++) {
      for (let y = kernelRadius; y < height - kernelRadius; y++) {
        for (let x = kernelRadius; x < width - kernelRadius; x++) {
          const idx = (y * width + x) * 4;
          const neighbors = [];

          // Collect 3x3 neighborhood
          for (let dy = -kernelRadius; dy <= kernelRadius; dy++) {
            for (let dx = -kernelRadius; dx <= kernelRadius; dx++) {
              const nIdx = ((y + dy) * width + (x + dx)) * 4;
              neighbors.push({
                r: dataCopy[nIdx],
                g: dataCopy[nIdx + 1],
                b: dataCopy[nIdx + 2],
                a: dataCopy[nIdx + 3],
              });
            }
          }

          // Sort and get median for each channel
          const rValues = neighbors.map((n) => n.r).sort((a, b) => a - b);
          const gValues = neighbors.map((n) => n.g).sort((a, b) => a - b);
          const bValues = neighbors.map((n) => n.b).sort((a, b) => a - b);

          const medianIdx = Math.floor(neighbors.length / 2);

          data[idx] = rValues[medianIdx];
          data[idx + 1] = gValues[medianIdx];
          data[idx + 2] = bValues[medianIdx];
          // Keep alpha unchanged
        }
      }
      // Update copy for next pass
      dataCopy.set(data);
    }

    ctx.putImageData(imageData, 0, 0);
  } catch (error) {
    console.error("Error applying median filter to canvas:", error);
  }
};

const PANEL_WIDTH = 480;

const getViewportContainerRight = () => {
  const el =
    document.getElementById("viewer-viewport-container") ||
    document.querySelector(".viewer-viewport-container");
  return el ? el.getBoundingClientRect().right : window.innerWidth;
};

// hideTrigger: render the panel + event listeners but NOT the desktop toolbar icon.
// Used when Image Enhancement is opened from the three-dot ("...") menu row instead of
// its own top-level icon (the row dispatches the `toggleImageEnhancement` event).
const ImageEnhancementTool = ({ hideTrigger = false } = {}) => {
  const { t } = useTranslation();
  const { showToastv2 } = useContext(toastContext);
  const { theme, getThemeColor } = useContext(themeContext);
  const { activeViewportIndex } = useContext(ViewportContext);
  const [showPanel, setShowPanel] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [sharpnessValue, setSharpnessValue] = useState(0);
  const [gaussianValue, setGaussianValue] = useState(0);
  const [medianValue, setMedianValue] = useState(0);
  const [edgeEnhanceValue, setEdgeEnhanceValue] = useState(0);
  const [activeEnhancements, setActiveEnhancements] = useState(new Set());
  // null until positioned — the panel is only rendered once this is set, so it
  // never paints at the stale/default spot before jumping to the right edge.
  const [panelPos, setPanelPos] = useState(null);
  const dragState = useRef({ dragging: false, startX: 0, startY: 0, originX: 0, originY: 0 });
  const panelRef = useRef(null);
  const viewportStateRef = useRef(new Map());
  // Drives the Apply button's disabled state. A ref (not just state) is used
  // as the re-entrancy guard because it updates synchronously — a state flag
  // wouldn't be visible to a second click fired before React re-renders the
  // button as disabled, so a fast double-click could still re-enter and apply
  // twice.
  const [isApplying, setIsApplying] = useState(false);
  const isApplyingRef = useRef(false);

  // Recompute position from live DOM every time the panel opens so it
  // always lands at the right edge of the viewer regardless of zoom level.
  // useLayoutEffect (not useEffect) so this runs synchronously before the
  // browser paints — combined with gating the panel's render on panelPos
  // being set, this prevents the one-frame flash on the left edge that a
  // post-paint useEffect correction would otherwise cause.
  useLayoutEffect(() => {
    if (!showPanel) {
      setPanelPos(null);
      return;
    }
    const topbar = document.querySelector(".topbar-container");
    const top = topbar ? topbar.getBoundingClientRect().bottom : 45;
    const containerRight = getViewportContainerRight();
    const panelW = Math.min(PANEL_WIDTH, containerRight - 8);
    setPanelPos({ x: Math.max(0, containerRight - panelW - 8), y: top });
  }, [showPanel]);

  const onDragStart = useCallback((e) => {
    // Ignore clicks on interactive elements inside the header
    if (e.target.closest("button")) return;
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: panelPos.x,
      originY: panelPos.y,
    };
    e.preventDefault();
  }, [panelPos]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragState.current.dragging) return;
      const dx = e.clientX - dragState.current.startX;
      const dy = e.clientY - dragState.current.startY;
      const panelW = panelRef.current?.offsetWidth || PANEL_WIDTH;
      const panelH = panelRef.current?.offsetHeight || 260;
      const containerRight = getViewportContainerRight();
      const newX = Math.min(Math.max(0, dragState.current.originX + dx), containerRight - panelW);
      const newY = Math.min(Math.max(0, dragState.current.originY + dy), window.innerHeight - panelH);
      setPanelPos({ x: newX, y: newY });
    };
    const onMouseUp = () => { dragState.current.dragging = false; };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  // Re-clamp position when window is resized so the panel never goes off-screen
  useEffect(() => {
    const onResize = () => {
      if (!showPanel) return;
      const panelW = panelRef.current?.offsetWidth || PANEL_WIDTH;
      const panelH = panelRef.current?.offsetHeight || 260;
      const containerRight = getViewportContainerRight();
      setPanelPos((prev) => {
        if (!prev) return prev;
        return {
          x: Math.min(prev.x, Math.max(0, containerRight - panelW)),
          y: Math.min(prev.y, Math.max(0, window.innerHeight - panelH)),
        };
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [showPanel]);

  /**
   * Listen for toggleImageEnhancement event dispatched from Viewer's onKeyPress (F key)
   */
  useEffect(() => {
    // Same guard as the toolbar icon's onClick — see there for why.
    const handleToggle = () => {
      if (isApplyingRef.current) return;
      setShowPanel((prev) => !prev);
    };
    document.addEventListener("toggleImageEnhancement", handleToggle);
    return () => document.removeEventListener("toggleImageEnhancement", handleToggle);
  }, []);

  /**
   * Listen for resetImageEnhancement event dispatched from the top bar's Reset tool
   */
  useEffect(() => {
    const handleExternalReset = () => resetEnhancements(true);
    document.addEventListener("resetImageEnhancement", handleExternalReset);
    return () => document.removeEventListener("resetImageEnhancement", handleExternalReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeViewportIndex]);

  useEffect(() => {
    const saved = viewportStateRef.current.get(activeViewportIndex);
    if (saved) {
      setSharpnessValue(saved.sharpness);
      setGaussianValue(saved.gaussian);
      setMedianValue(saved.median);
      setEdgeEnhanceValue(saved.edgeEnhance);
      setActiveEnhancements(new Set(saved.activeEnhancements));
    } else {
      setSharpnessValue(0);
      setGaussianValue(0);
      setMedianValue(0);
      setEdgeEnhanceValue(0);
      setActiveEnhancements(new Set());
    }
  }, [activeViewportIndex]);

  /**
   * Median filter and edge enhancement are manual CPU paints onto the viewport's
   * <canvas> — they are NOT part of Cornerstone's render pipeline (unlike
   * sharpening/smoothing, which are real viewport properties re-applied on every
   * render). So the next time ANYTHING re-renders this viewport — scrolling to
   * another slice, panning, zooming, adjusting window/level, syncing, etc. — the
   * GPU frame gets re-blitted onto that same canvas and silently erases the paint.
   * This keeps reapplying both filters (median then edge, matching the apply order)
   * after every subsequent render, for as long as either is active on this viewport.
   */
  useEffect(() => {
    if (medianValue <= 0 && edgeEnhanceValue <= 0) return;
    let cancelled = false;
    let element = null;
    let onRendered = null;

    (async () => {
      const viewport = await getViewport(activeViewportIndex);
      if (cancelled || !viewport || !isStackViewport(viewport)) return;
      element = viewport.element;
      if (!element) return;

      onRendered = () => {
        const canvas = element.querySelector("canvas");
        if (!canvas) return;
        if (medianValue > 0) applyMedianFilterToCanvas(canvas, medianValue);
        if (edgeEnhanceValue > 0) applyEdgeEnhancementToCanvas(canvas, edgeEnhanceValue);
      };
      element.addEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
    })();

    return () => {
      cancelled = true;
      if (element && onRendered) {
        element.removeEventListener(Enums.Events.IMAGE_RENDERED, onRendered);
      }
    };
  }, [activeViewportIndex, medianValue, edgeEnhanceValue]);

  /**
   * Apply sharpening using Cornerstone's public setProperties() API
   * Uses GPU-accelerated unsharp masking (3x3 kernel)
   * silent=true skips toast and active enhancement state update (used when batching)
   */
  const applySharpening = async (value, silent = false) => {
    try {
      const viewport = await getViewport(activeViewportIndex);

      if (!viewport) {
        if (!silent) showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return false;
      }

      if (isStackViewport(viewport)) {
        // Scaled 10x to match the smoothingRenderPass intensity range — see
        // applyGaussianBlur for why (both setProperties calls re-send both
        // properties together, so they must stay consistent).
        viewport.setProperties({ sharpening: value, smoothing: gaussianValue * 10 });
      } else if (isVolumeViewport(viewport)) {
        applyVolumeEnhancement(viewport, "sharpening", value);
      } else {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error applying sharpening:", error);
      return false;
    }
  };

  /**
   * Apply Gaussian blur using Cornerstone's public setProperties() API
   * Uses GPU-accelerated Gaussian blur (15x15 kernel, sigma=5.0)
   * silent=true skips toast and active enhancement state update (used when batching)
   */
  const applyGaussianBlur = async (value, silent = false) => {
    try {
      const viewport = await getViewport(activeViewportIndex);

      if (!viewport) {
        if (!silent) showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return false;
      }

      if (isStackViewport(viewport)) {
        // Cornerstone's smoothingRenderPass blends at alpha = min(intensity / 10, 1),
        // i.e. it expects an intensity roughly in the 0-10 range for a full-strength
        // blur. Our slider sends 0-1, so scale up 10x here — otherwise the blur maxes
        // out at 10% strength and is barely perceptible across the whole slider range.
        viewport.setProperties({ smoothing: value * 10, sharpening: sharpnessValue });
      } else if (isVolumeViewport(viewport)) {
        applyVolumeEnhancement(viewport, "smoothing", value);
      } else {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error applying Gaussian blur:", error);
      return false;
    }
  };

  /**
   * Apply median filter using CPU-based 3x3 kernel processing
   * silent=true skips toast and active enhancement state update (used when batching)
   */
  const applyMedianFilter = async (value, silent = false) => {
    try {
      const viewport = await getViewport(activeViewportIndex);

      if (!viewport) {
        if (!silent) showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return false;
      }

      if (isStackViewport(viewport)) {
        const canvas = viewport.element?.querySelector("canvas");
        if (canvas) {
          // No viewport.render() here — see applyAllEnhancements, which awaits the
          // GPU render (sharpening/smoothing) via waitForNextRender() BEFORE calling
          // this, so the canvas is already showing the latest frame. Calling render()
          // in here would just schedule another deferred repaint that arrives after
          // this manual edit and wipes it out again.
          applyMedianFilterToCanvas(canvas, value);
        } else {
          return false;
        }
      } else if (isVolumeViewport(viewport)) {
        applyVolumeEnhancement(viewport, "median", value);
      } else {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error applying median filter:", error);
      return false;
    }
  };

  /**
   * Apply edge enhancement using CPU-based 3x3 Laplacian kernel
   * silent=true skips toast and active enhancement state update (used when batching)
   */
  const applyEdgeEnhancement = async (value, silent = false) => {
    try {
      const viewport = await getViewport(activeViewportIndex);

      if (!viewport) {
        if (!silent) showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return false;
      }

      if (isStackViewport(viewport)) {
        const canvas = viewport.element?.querySelector("canvas");
        if (canvas) {
          // Same reasoning as applyMedianFilter — no render() call here, it must run
          // strictly after the GPU render has landed (see waitForNextRender below).
          applyEdgeEnhancementToCanvas(canvas, value);
        } else {
          return false;
        }
      } else if (isVolumeViewport(viewport)) {
        applyVolumeEnhancement(viewport, "edgeEnhance", value);
      } else {
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error applying edge enhancement:", error);
      return false;
    }
  };

  /**
   * Resolves once this viewport's next GPU render has actually landed on the
   * canvas (Cornerstone defers rendering to the next animation frame — see
   * applyAllEnhancements for why this matters), or after timeoutMs as a
   * safety net so a missed/suppressed event can never hang "Apply".
   */
  const waitForNextRender = (viewport, timeoutMs = 500) =>
    new Promise((resolve) => {
      const element = viewport?.element;
      if (!element) {
        resolve();
        return;
      }
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        element.removeEventListener(Enums.Events.IMAGE_RENDERED, finish);
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(finish, timeoutMs);
      element.addEventListener(Enums.Events.IMAGE_RENDERED, finish);
    });

  /**
   * Apply all filters at once and show a single consolidated toast
   */
  const applyAllEnhancements = async () => {
    // Re-entrancy guard: the ref check is synchronous, so a second click fired
    // before React re-renders the button as disabled is still blocked here.
    if (isApplyingRef.current) return;
    isApplyingRef.current = true;
    setIsApplying(true);

    try {
      // Panel (and the Apply button) stays open and visible while the filters are
      // being applied — it used to close immediately on click, which meant the
      // disabled state below was never actually seen; only the toolbar icon
      // reverting to its inactive color during the wait was visible, which read as
      // the icon itself being disabled.
      const viewport = await getViewport(activeViewportIndex);

      // Phase 1: GPU-based filters (sharpening/smoothing render passes). Cornerstone's
      // setSharpening/setSmoothing unconditionally call this.render() — which only
      // *schedules* a render on the next animation frame, it doesn't paint synchronously.
      const [sharpeningOk, gaussianOk] = await Promise.all([
        applySharpening(sharpnessValue, true),
        applyGaussianBlur(gaussianValue, true),
      ]);

      // Phase 2: CPU-based canvas filters (median, edge). These paint directly onto the
      // viewport's <canvas>, but that canvas gets overwritten with the freshly-rendered
      // GPU frame the instant phase 1's deferred render fires. Waiting for that render
      // to actually land first — then running median and edge sequentially, not
      // concurrently with each other — ensures these paints are the last thing to touch
      // the canvas instead of being silently wiped out.
      if (viewport && isStackViewport(viewport)) {
        await waitForNextRender(viewport);
      }
      const medianOk = await applyMedianFilter(medianValue, true);
      const edgeOk = await applyEdgeEnhancement(edgeEnhanceValue, true);

      const results = [sharpeningOk, gaussianOk, medianOk, edgeOk];

      const anySuccess = results.some(Boolean);

      if (!anySuccess) {
        showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return;
      }

      const newEnhancements = new Set();
      if (sharpnessValue > 0) newEnhancements.add("sharpening");
      if (gaussianValue > 0) newEnhancements.add("gaussian");
      if (medianValue > 0) newEnhancements.add("median");
      if (edgeEnhanceValue > 0) newEnhancements.add("edgeEnhance");
      setActiveEnhancements(newEnhancements);

      viewportStateRef.current.set(activeViewportIndex, {
        sharpness: sharpnessValue,
        gaussian: gaussianValue,
        median: medianValue,
        edgeEnhance: edgeEnhanceValue,
        activeEnhancements: [...newEnhancements],
      });

      if (newEnhancements.size === 0) {
        showToastv2(TOAST_TYPE.SUCCESS, t("viewer.tools.ie-reset-success", "All enhancement filters cleared"));
      } else {
        showToastv2(
          TOAST_TYPE.SUCCESS,
          t("viewer.tools.ie-applied-count", "{{count}} enhancement filter{{suffix}} applied", {
            count: newEnhancements.size,
            suffix: newEnhancements.size !== 1 ? "s" : "",
          })
        );
      }
    } finally {
      isApplyingRef.current = false;
      setIsApplying(false);
      setShowPanel(false);
    }
  };

  /**
   * Reset all enhancements using public setProperties() API and clear custom render passes.
   * silent=true skips every toast — used when the top bar's Reset tool triggers this via
   * the resetImageEnhancement event, where a silent no-op reset shouldn't be reported.
   */
  const resetEnhancements = async (silent = false) => {
    try {
      const viewport = await getViewport(activeViewportIndex);

      if (!viewport) {
        if (!silent) showToastv2(TOAST_TYPE.WARNING, t("viewer.tools.ie-no-viewport", "No active viewport found"));
        return;
      }

      try {
        if (isStackViewport(viewport)) {
          viewport.setProperties({ sharpening: 0, smoothing: 0 });
          viewport._customRenderPass = null;
          viewport.render();
        } else if (isVolumeViewport(viewport)) {
          viewport.setInterpolationType(Enums.InterpolationType.LINEAR);
          viewport.resetProperties();
        }
      } catch (error) {
        console.error("Error resetting viewport:", error);
      }

      setSharpnessValue(0);
      setGaussianValue(0);
      setMedianValue(0);
      setEdgeEnhanceValue(0);
      setActiveEnhancements(new Set());
      viewportStateRef.current.delete(activeViewportIndex);

      if (!silent) showToastv2(TOAST_TYPE.SUCCESS, t("viewer.tools.ie-reset-success", "All enhancement filters cleared"));
    } catch (error) {
      console.error("Error resetting enhancements:", error);
      if (!silent) showToastv2(TOAST_TYPE.ERROR, t("viewer.tools.ie-reset-failed", "Failed to reset enhancements"));
    }
  };

  //  on mobile there is no toolbar icon — the panel is opened from the
  // bottom-toolbar "More" menu via the `toggleImageEnhancement` event (listener above). Render
  // it as a bottom sheet, reusing the same ImageEnhancementBody + apply/reset logic as desktop.
  if (isMobile) {
    if (!showPanel) return null;
    return (
      <div className="ie-mobile-sheet-overlay" onClick={() => setShowPanel(false)}>
        <div className="ie-mobile-sheet" onClick={(e) => e.stopPropagation()}>
          <div
            className="ie-mobile-sheet-header"
            style={{ background: getThemeColor("modal-header-footer") }}
          >
            <span className="ie-overlay-title" style={{ color: getThemeColor("primary-color") }}>
              {t("viewer.tools.image-enhancement", "Image Enhancement")}
            </span>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              ariaLabel="Close"
              onClick={() => setShowPanel(false)}
              styles={{
                icon: { color: getThemeColor("primary-color"), fontSize: 12 },
                root: { backgroundColor: "transparent" },
                rootHovered: { backgroundColor: "transparent" },
                rootPressed: { backgroundColor: "transparent" },
              }}
            />
          </div>
          <div
            className="ie-mobile-sheet-body"
            style={{ background: getThemeColor("modal-body"), color: getThemeColor("primary-color") }}
          >
            <ImageEnhancementBody
              activeEnhancements={activeEnhancements}
              sharpnessValue={sharpnessValue}
              setSharpnessValue={setSharpnessValue}
              gaussianValue={gaussianValue}
              setGaussianValue={setGaussianValue}
              medianValue={medianValue}
              setMedianValue={setMedianValue}
              edgeEnhanceValue={edgeEnhanceValue}
              setEdgeEnhanceValue={setEdgeEnhanceValue}
            />
          </div>
          <div className="ie-mobile-sheet-footer" style={{ background: getThemeColor("modal-header-footer") }}>
            <DefaultButton text={t("viewer.tools.ie-reset-all", "Reset All")} onClick={() => resetEnhancements()} style={{ marginRight: 8 }} />
            <PrimaryButton text={t("button.apply", "Apply")} onClick={applyAllEnhancements} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {/* NEW: hideTrigger skips the top-level icon when opened from the "..." menu row */}
      {!hideTrigger && (
      <CustomToolTipHost
        id="enhance-tool"
        content={t("viewer.tools.image-enhancement")}
        delay={TooltipDelay.zero}
        directionalHint={DirectionalHint.bottomCenter}
        styles={toolTipStyles}
        className="myToolTip"
      >
        <div className="highlight-tool">
          <div
            style={toolBarFlex}
            onMouseOver={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onClick={() => {
              // Ignore re-toggles while a previous Apply is still finishing — its
              // finally block will force the panel closed once it resolves, which
              // would otherwise clobber a reopen attempted in that window.
              if (isApplyingRef.current) return;
              setShowPanel(!showPanel);
            }}
          >
            <ImageEnhancementIcon
              color={
                hovered || showPanel
                  ? theme.palette.activeTool
                  : theme.palette.inactiveTool
              }
            />
          </div>
        </div>
      </CustomToolTipHost>
      )}

      {showPanel && panelPos && (
        <div
          ref={panelRef}
          className="ie-overlay-panel"
          style={{ left: panelPos.x, top: panelPos.y }}
        >
          <div
            className="ie-overlay-header"
            style={{ background: getThemeColor("modal-header-footer") }}
            onMouseDown={onDragStart}
          >
            <span
              className="ie-overlay-title"
              style={{ color: getThemeColor("primary-color") }}
            >
              {t("viewer.tools.image-enhancement", "Image Enhancement")}
            </span>
            <IconButton
              iconProps={{ iconName: "Cancel" }}
              ariaLabel="Close"
              onClick={() => setShowPanel(false)}
              styles={{
                icon: { color: getThemeColor("primary-color"), fontSize: 12 },
                root: { backgroundColor: "transparent" },
                rootHovered: { backgroundColor: "transparent" },
                rootPressed: { backgroundColor: "transparent" },
              }}
            />
          </div>

          <div
            className="ie-overlay-body"
            style={{
              background: getThemeColor("modal-body"),
              color: getThemeColor("primary-color"),
            }}
          >
            <ImageEnhancementBody
              activeEnhancements={activeEnhancements}
              sharpnessValue={sharpnessValue}
              setSharpnessValue={setSharpnessValue}
              gaussianValue={gaussianValue}
              setGaussianValue={setGaussianValue}
              medianValue={medianValue}
              setMedianValue={setMedianValue}
              edgeEnhanceValue={edgeEnhanceValue}
              setEdgeEnhanceValue={setEdgeEnhanceValue}
            />
          </div>

          <div className="ie-overlay-footer" style={{ background: getThemeColor("modal-header-footer") }}>
            <DefaultButton
              text={t("button.close", "Close")}
              onClick={() => setShowPanel(false)}
              disabled={isApplying}
              style={{ marginRight: 8 }}
            />
            <DefaultButton
              text={t("viewer.tools.ie-reset-all", "Reset All")}
              onClick={() => resetEnhancements()}
              disabled={isApplying}
              style={{ marginRight: 8 }}
            />
            <PrimaryButton
              text={t("button.apply", "Apply")}
              onClick={applyAllEnhancements}
              disabled={isApplying}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageEnhancementTool;
